import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from './base.entity';

/**
 * Which customer-facing documents this rule copies people on (PD-15003).
 *
 * Modelled as a column rather than one table per document so adding
 * statements or order confirmations later is a row, not a migration.
 */
export enum CopyDocumentType {
	INVOICE = 'invoice',
	DELIVERY_RECEIPT = 'delivery_receipt',
	/**
	 * PD-15079 — a credit memo emailed from the credit memo screen. Its own
	 * type so a tenant can route credits differently (they are money going
	 * OUT), but the resolver falls back to the INVOICE rules when no
	 * credit-memo rules are configured, so no tenant has to configure the
	 * same recipients twice.
	 */
	CREDIT_MEMO = 'credit_memo',
	/**
	 * PD-15417 — sensor alert emails (temperature excursions and unresponsive
	 * units). Not a customer document: the recipients ARE the audience (there
	 * is no customer To line), so the resolver's cc+bcc lists are used as the
	 * To recipients by the alert sender.
	 *
	 * PD-15494 — for that reason it is configured on the Sensor Alerts settings
	 * screen next to the thresholds, NOT on Document Copies, and is absent from
	 * DOCUMENT_COPY_TYPES. That screen edits a single audience list and writes
	 * it to cc, so bcc is only ever non-empty on records predating the move;
	 * the screen folds both together when it loads. Same arrangement as
	 * PAYMENTS_DIGEST below.
	 */
	SENSOR_ALERT = 'sensor_alert',
	/**
	 * PD-15317 — the internal Daily Payments Digest (merchant settlement
	 * health: attention orders, refunds owed, settlement failures). Not a
	 * customer-facing document: recipients are configured on the Payment
	 * Settings screen rather than the Document Copies screen (so it is
	 * deliberately absent from DOCUMENT_COPY_TYPES). Deliberately NO
	 * fallback to the INVOICE rules (owner direction 2026-08-08): merchant
	 * settlement information goes only to people explicitly configured for
	 * it — an unconfigured digest simply does not send.
	 */
	PAYMENTS_DIGEST = 'payments_digest',
	/**
	 * PD-16306 — the weekly compliance summary email: the Compliance
	 * dashboard's Verified Coverage and one line per Needs Attention card.
	 * Internal, like PAYMENTS_DIGEST: the recipients ARE the audience, it is
	 * configured on Notification Recipients rather than with the customer
	 * documents (so it is absent from DOCUMENT_COPY_TYPES), and the screen
	 * edits one list stored under cc. Deliberately NO fallback: a tenant with
	 * nobody configured, which is every tenant until someone is added, is not
	 * sent the summary at all.
	 */
	COMPLIANCE_SUMMARY = 'compliance_summary',
	/**
	 * PD-16841 — the "Your order has been submitted" confirmation for an order
	 * placed on the storefront. Customer-facing, like INVOICE: the buyer is on
	 * the To line and these recipients join them on the line the tenant chose.
	 * Website orders only. CRM order confirmations are sent by staff who
	 * already know about the order, and copying a shared mailbox on all of
	 * them would bury the web orders it is meant to catch. No fallback to any
	 * other list.
	 */
	WEBSITE_ORDER = 'website_order',
}

/**
 * Which line of the email a configured recipient goes on.
 *
 * PD-16841 — TO added so a tenant can put its own people on the To line
 * beside the customer. The settings screen applies one line to a whole list
 * of a customer document, so every row of one document type carries the
 * same mode; the resolver still honors a mixed set, which only ever exists
 * in data saved before that rule.
 */
export enum CopyMode {
	TO = 'to',
	CC = 'cc',
	BCC = 'bcc',
}

/**
 * PD-15003 — who at the TENANT gets copied when a customer-facing document is
 * emailed out, configured per tenant.
 *
 * Before this, copying a colleague meant typing their address into the send
 * dialog every time, and the automatic delivery receipt had no way to copy
 * anyone at all.
 *
 * A rule names exactly one recipient by one of three means, in the order the
 * resolver prefers them:
 *
 *   - `fkRbacRoleId` — every active member of a role ("copy Accounting").
 *     Membership is resolved at send time, so staff changes need no edit here.
 *   - `fkUserId` — one named person, for someone who should be copied
 *     regardless of their role.
 *   - `emailAddress` — a plain address. Needed because a shared mailbox
 *     (accounting@…) is usually not a system user at all, and because a role
 *     can be empty: on Motor City Ice the Accounting role has zero members
 *     while Admin has eight, so a role-only design would let someone configure
 *     a rule that silently copies nobody.
 *
 * Exactly one of the three is set per row; the others are null.
 */
@Index('idx_document_copy_recipient_document', ['documentType', 'isActive'], {})
@Entity('document_copy_recipient')
export class DocumentCopyRecipientEntity extends BaseEntity {
	@Column('varchar', { name: 'documentType', length: 40 })
	public documentType!: CopyDocumentType;

	@Column('varchar', { name: 'copyMode', length: 10, default: CopyMode.BCC })
	public copyMode!: CopyMode;

	/** FK to _rbac_role._id — every active member is copied. */
	@Column('int', { name: 'fk__rbac_role__id', unsigned: true, nullable: true })
	public fkRbacRoleId!: number | null;

	/** FK to user._id — one named person. */
	@Column('int', { name: 'fk_user__id', unsigned: true, nullable: true })
	public fkUserId!: number | null;

	/** A plain address, for shared mailboxes and non-users. */
	@Column('varchar', { name: 'emailAddress', length: 255, nullable: true })
	public emailAddress!: string | null;

	@Column('tinyint', { name: 'isActive', width: 1, default: 1 })
	public isActive!: boolean;
}
